import React, { useRef, useEffect, useState } from "react";
import { motion, useAnimation, useInView, AnimatePresence } from "framer-motion";

// Explicitly mention all image names in the array
const IMAGE_PATHS = [
 
  "/Images/image4.jpeg",
  "/Images/image5.jpeg",
  "/Images/image6.jpeg",
  "/Images/image7.jpeg",
  "/Images/image8.jpeg",
  "/Images/image10.jpeg",
  "/Images/image12.jpeg",
  "/Images/image13.jpeg",
  "/Images/image14.jpeg",
  "/Images/image16.jpeg",
  "/Images/image19.jpeg",

];

const FADE_DURATION = 0.8; // seconds
const SLIDE_DURATION = 3.0; // seconds

const HeroSection = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });
  const controls = useAnimation();

  // State for cycling images
  const [bgIndex, setBgIndex] = useState(0);
  const [prevBgIndex, setPrevBgIndex] = useState(0);
  const [fadeKey, setFadeKey] = useState(0); // Key ensures new AnimatePresence for each transition

  useEffect(() => {
    if (isInView) controls.start("visible");
  }, [controls, isInView]);

  useEffect(() => {
    const interval = setInterval(() => {
      setPrevBgIndex(bgIndex);
      setBgIndex((i) => (i + 1) % IMAGE_PATHS.length);
      setFadeKey((k) => k + 1);
    }, SLIDE_DURATION * 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [bgIndex]);

  const headingVariant = {
    hidden: { opacity: 0, y: 50 },
    visible: (i = 1) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.2, duration: 0.8, ease: "easeOut" },
    }),
  };

  const paragraphVariant = {
    hidden: { opacity: 0, y: 20 },
    visible: (i = 1) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.25, duration: 0.8, ease: "easeOut" },
    }),
  };

  const buttonVariant = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: "backOut" },
    },
    hover: { scale: 1.05 },
  };

  // Preload current and next images for smooth transitions
  useEffect(() => {
    const img1 = new window.Image();
    img1.src = IMAGE_PATHS[bgIndex];
    const img2 = new window.Image();
    img2.src = IMAGE_PATHS[(bgIndex + 1) % IMAGE_PATHS.length];
  }, [bgIndex]);

  // BG slide animation variants
  const backgroundVariants = {
    initial: { opacity: 0, scale: 1.02 },
    animate: { opacity: 1, scale: 1, transition: { duration: FADE_DURATION } },
    exit: { opacity: 0, scale: 0.98, transition: { duration: FADE_DURATION } },
  };

  return (
    <section
      ref={containerRef}
      className="relative bg-gradient-to-br from-[#08172A] to-[#0D2A4A] min-h-screen flex items-center overflow-hidden font-sans"
      style={{}}
    >
      {/* Animated backgrounds in stack */}
      <div className="absolute inset-0 w-full h-full pointer-events-none z-0">
        <AnimatePresence initial={false} mode="wait">
          <motion.div
            key={fadeKey}
            variants={backgroundVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="absolute inset-0 w-full h-full"
            style={{
              backgroundImage: `url('${IMAGE_PATHS[bgIndex]}')`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}
          />
        </AnimatePresence>
        {/* Fallback solid overlay for contrast */}
        <div className="absolute inset-0 bg-black opacity-50"></div>
      </div>
      {/* Decorative Background Shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
        <motion.div
          className="absolute w-60 h-60 bg-gradient-to-tr from-[#4FB1FF]/20 to-[#FFD700]/20 rounded-full top-20 left-10 blur-3xl"
          animate={{ rotate: 360 }}
          transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          className="absolute w-72 h-72 bg-gradient-to-bl from-[#FFD700]/15 to-[#4FB1FF]/15 rounded-full bottom-10 right-20 blur-3xl"
          animate={{ rotate: -360 }}
          transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          className="absolute w-24 h-24 bg-white/10 rounded-full top-1/2 left-1/4 blur-2xl"
          animate={{ scale: [1, 1.05, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Main Container */}
      <div className="container mx-auto flex flex-col-reverse lg:flex-row items-center justify-between px-6 py-20 z-20 relative">
        {/* Left Text */}
        <div className="flex-1 text-left mt-10 lg:mt-0 space-y-6">
          <motion.h1
            custom={1}
            initial="hidden"
            animate={controls}
            variants={headingVariant}
            className="text-3xl sm:text-4xl font-Agatho_Regular  md:text-5xl lg:text-6xl font-extrabold text-white leading-tight tracking-wide"
          >
            Fostering Unity <br className="hidden md:block" />
            Through <span className="text-[#FFD700]">Global Partnerships</span>
          </motion.h1>

          <motion.p
            custom={2}
            initial="hidden"
            animate={controls}
            variants={paragraphVariant}
            className="text-gray-200 text-base sm:text-lg md:text-xl max-w-xl leading-relaxed"
          >
            Connecting cultures, commerce, and communities for sustainable growth.
          </motion.p>

          <motion.p
            custom={3}
            initial="hidden"
            animate={controls}
            variants={paragraphVariant}
            className="text-gray-200 text-base sm:text-lg md:text-xl max-w-xl leading-relaxed"
          >
            We cultivate shared prosperity and collaborative innovation across continents.
          </motion.p>

          <motion.div
            className="flex flex-wrap gap-4 mt-4"
            initial="hidden"
            animate={controls}
            variants={{ visible: { transition: { staggerChildren: 0.15 } } }}
          >
            <motion.a
              href="/services"
              className="bg-[#4FB1FF] text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:bg-[#3A93D9] transition inline-block"
              variants={buttonVariant}
              whileHover="hover"
            >
             Services
            </motion.a>
            <motion.a
              href="/about"
              className="border border-[#4FB1FF] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#4FB1FF]/20 transition inline-block"
              variants={buttonVariant}
              whileHover="hover"
            >
              About Us
            </motion.a>
       
          </motion.div>
        </div>

        {/* Right Visual */}
        <motion.div
          className="flex-1 flex justify-center items-center relative mb-12 lg:mb-0"
          initial={{ opacity: 0, y: 60, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1, delay: 0.4, ease: "easeOut" }}
        >
          <motion.div
            className="absolute w-[320px] sm:w-[400px] md:w-[460px] lg:w-[520px] h-[320px] sm:h-[400px] md:h-[460px] lg:h-[520px] bg-gradient-to-tr from-[#4FB1FF]/20 to-[#FFD700]/20 rounded-full blur-2xl"
            animate={{ rotate: 360 }}
            transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
          />
          {/* Changed image to match the current background image */}
          <img
            src={IMAGE_PATHS[bgIndex]}
            alt="Global Collaboration"
            className="relative w-[280px] sm:w-[360px] md:w-[420px] lg:w-[600px] bg-white p-1 rounded-2xl z-10 drop-shadow-2xl"
            key={fadeKey} // Remount for fade
            style={{
              transition: `opacity ${FADE_DURATION}s`,
              opacity: 1, // Always visible; extra effects can be added if needed
            }}
          />
        </motion.div>
      </div>

      {/* Overlay Gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#08172A] via-transparent to-transparent opacity-70 pointer-events-none z-30" />
    </section>
  );
};

export default HeroSection;
